<template>
  <div
    v-if="hasContratoPendente"
    class="contratos-pendentes-banner"
    role="status"
  >
    <span class="contratos-pendentes-banner__text-wrapper">
      <img
        src="@/ui/assets/icons/atencao.png"
        alt="Icone de atenção"
        class="contratos-pendentes-banner__icon"
      />
      <span class="contratos-pendentes-banner__text">
        <template v-if="isTermoAutomatico">
          <b>{{ nomeTermoPendenteNormalizado }}:</b> os termos foram atualizados. O uso
          continuado da plataforma implica no consentimento das alterações.
        </template>
        <template v-else-if="podeExibirAcaoAssinatura && isBloqueioPorPrazo">
          <b>{{ nomeTermoPendenteNormalizado }}:</b> revise e aceite para voltar a usar a
          plataforma.
        </template>
        <template v-else-if="podeExibirAcaoAssinatura">
          <b>Termos e condições gerais atualizadas:</b> revise as alterações e confirme
          seu aceite.
        </template>
        <template v-else>
          <b>{{ nomeTermoPendenteNormalizado }}:</b> revise as alterações do termo.
        </template>
      </span>
    </span>
    <b>{{ prazoLimiteAssinaturaPendente }}</b>
    <PButton
      v-if="podeExibirAcaoAssinatura || isTermoAutomatico"
      size="small"
      type="link"
      suffix-icon="arrow_forward"
      filled
      class="contratos-pendentes-banner__cta"
      @click="irParaGestaoContratos"
    >
      <b>{{ isTermoAutomatico ? "Visualizar" : "Revisar e aceitar" }}</b>
    </PButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { F360Product } from "@/core/@types/types";
import useHostNavigation from "@/core/composables/useHostNavigation";
import useF360Router from "@/core/router/useF360Router";
import { useContratoPendenteStore } from "@/core/store/contratos/useContratoPendenteStore";
import { EGestaoContratos } from "@FIN/views/gestaoContratos/router/types";
import { PButton } from "@f360-dev/f360-prisma";
import { storeToRefs } from "pinia";

const contratoPendenteStore = useContratoPendenteStore();

const props = withDefaults(
  defineProps<{
    navigationMode?: "vue" | "mvc";
    vueBaseUrl?: string;
  }>(),
  {
    navigationMode: "vue",
    vueBaseUrl: "/v2/#"
  }
);

const {
  prazoLimiteAssinaturaPendente,
  isBloqueioPorPrazo,
  isTermoAutomatico,
  isPendente: hasContratoPendente,
  lastTerm,
  podeAssinarTermoPendente: podeExibirAcaoAssinatura
} = storeToRefs(contratoPendenteStore);
const { handlePrefixName } = useF360Router();
const { navigateTo } = useHostNavigation({
  navigationMode: () => props.navigationMode,
  vueBaseUrl: () => props.vueBaseUrl
});

const nomeTermoPendenteNormalizado = computed(() => {
  const nome = lastTerm.value?.nome || lastTerm.value?.tipo || "Termo atualizado";

  return nome.charAt(0).toUpperCase() + nome.slice(1).toLowerCase();
});

function irParaGestaoContratos() {
  if (
    !lastTerm.value ||
    (!podeExibirAcaoAssinatura.value && !isTermoAutomatico.value)
  ) {
    return;
  }

  navigateTo({
    name: handlePrefixName(F360Product.Financas, EGestaoContratos.Index),
    query: {
      abrirContratoPendente: "1"
    }
  });
}
</script>

<style lang="scss" scoped>
.contratos-pendentes-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px;
  margin-top: 8px;
  border-radius: 5px;
  font-size: 14px;
  background-color: _map.get($map: $amarelo, $key: 100);
  color: _map.get($map: $neutro, $key: 950);
}

.contratos-pendentes-banner__text-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  min-width: 200px;
}

.contratos-pendentes-banner__icon {
  width: 24px;
  height: 24px;
}

.contratos-pendentes-banner__text {
  margin: 0;
  flex: 1;
  min-width: 200px;
  font-size: 14px;
  line-height: 1.4;
}

.contratos-pendentes-banner__cta b {
  flex-shrink: 0;
  font-size: 14px;
}
</style>
